// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useRef } from 'react';

import type { Job } from '../../api/types';
import { isTerminal } from '../../api/types';
import { newlySucceededImports } from '../../bygning/bimImport';
import { useJobs } from '../JobsContext';

/**
 * Calls `onFinished` with each BIM import (`import_speckle`, `import_ifc`)
 * that succeeds while the screen is open — once per job, from any client.
 * The jobs already finished at the first `hello` are history, not news
 * (`JobsContext.history`).
 */
export function useImportFinished(onFinished: (jobs: Job[]) => void): void {
  const { jobs, history } = useJobs();
  const seen = useRef<Set<string> | null>(null);
  const callback = useRef(onFinished);
  callback.current = onFinished;

  useEffect(() => {
    if (history === null) return;
    if (seen.current === null) seen.current = new Set(history);
    const fresh = newlySucceededImports(seen.current, jobs);
    for (const j of jobs) if (isTerminal(j.status)) seen.current.add(j.id);
    if (fresh.length > 0) callback.current(fresh);
  }, [jobs, history]);
}
